import type { QueueEntry } from '@ooh/contracts';
import { formatDisplayDate, formatDisplayDateTime } from '@ooh/ui/lib/date-format';
import type { QueueEntryView } from '../components/reservation-queue-list';
import { entryOverlapsReservation } from './queue-overlap';

export interface QueueWindow {
  dateFrom: string;
  dateTo: string;
}

/**
 * The one QueueEntry -> QueueEntryView mapping, shared by the unit panel, the
 * global reservations list and the availability-cell popover.
 *
 * `entries` MUST be the whole queue for the unit (or the reservation's batched
 * group), never a caller-filtered subset: position is the entry's place in that
 * FCFS line, so filtering first would renumber it. `siblingWindows` are the
 * windows of the viewer's reservations on the SAME unit; an entry overlapping
 * more than one of them gets `spansMultiple`.
 */
export function mapQueueEntriesToViews(
  entries: QueueEntry[],
  window: QueueWindow,
  siblingWindows: QueueWindow[],
): QueueEntryView[] {
  return entries
    .filter((entry) => entryOverlapsReservation(entry, window))
    .sort((a, b) => a.position - b.position)
    .map((entry, index) => ({
      id: entry.id,
      position: index + 1,
      userId: entry.holderUserId,
      userName: entry.holderUserName,
      offerName: entry.offerName,
      offerHref: entry.offerId !== null ? `/offers/${String(entry.offerId)}` : null,
      clientName: entry.clientName,
      brand: entry.brand,
      salespersonName: entry.salespersonName,
      dateFrom: formatDisplayDate(entry.dateFrom),
      dateTo: formatDisplayDate(entry.dateTo),
      spansMultiple: siblingWindows.filter((w) => entryOverlapsReservation(entry, w)).length > 1,
      subtype: entry.subtype,
      joinedAtLabel: formatDisplayDateTime(entry.requestedAt),
      promotionEtaLabel:
        entry.promotionEligibleAt !== null ? formatDisplayDate(entry.promotionEligibleAt) : null,
    }));
}
